/* eslint-disable react-hooks/refs */
/* eslint-disable react-hooks/set-state-in-effect */
/**
 * useNotifications — central hook for the notification bell + inbox.
 *
 * Strategy:
 * - On mount, fetch an initial snapshot via REST.
 * - Only the primary visible tab opens the SSE stream; other tabs receive
 *   live inbox/count updates over BroadcastChannel.
 * - Poll REST only from the stream owner while SSE is down (never while healthy).
 */

import React, { createContext, useContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import {
  notificationsApi,
  subscribeToNotifications,
  type NotificationItem,
  type NotificationPreferences,
  type NotificationStreamConnection,
} from '../../services/api/notifications'
import {
  registerNotificationSoundUnlock,
} from '../../utils/notificationSound'
import { clientUniqueId } from '../../utils/secureRandom'
import { notificationAlertCoordinator } from './notificationAlertCoordinator'
import {
  getNotificationToastKey,
  shouldSuppressAgentResponseToast,
} from './notificationToastPolicy'
import {
  initNotificationToastTabGate,
  publishNotificationTabSync,
  subscribeNotificationStreamOwnership,
  subscribeNotificationTabSync,
  type NotificationTabSyncMessage,
  shouldShowNotificationToastInThisTab,
} from './notificationToastTabGate'

const POLL_INTERVAL_MS = 30000
const PAGE_SIZE = 15
const MAX_ITEMS = 100

const DEFAULT_PREFERENCES: NotificationPreferences = {
  in_app_enabled: true,
  sound_enabled: true,
  email_enabled: false,
  email_categories: {
    workflow_run: false,
    cwb_complete: false,
    aba_complete: false,
    agent_response: false,
    access_decision: false,
    user_registration: false,
    benchmark_assignment: false,
    knowledge_config: false,
  },
}

function mergeItem(items: NotificationItem[], incoming: NotificationItem): NotificationItem[] {
  // Real-time notifications triggered from inner pages may not contain a unique "id" field in their SSE payload.
  // We automatically generate a unique client-side ID to prevent them from filtering each other out during merge.
  const itemId = incoming.id || clientUniqueId('notif-temp')
  const itemWithId = { ...incoming, id: itemId }

  const without = items.filter((n) => n.id !== itemWithId.id)
  return [itemWithId, ...without].slice(0, MAX_ITEMS)
}

/** Merge SSE snapshot rows into the inbox without dropping paginated older items. */
function mergeSnapshotItems(prev: NotificationItem[], snapshotItems: NotificationItem[]): NotificationItem[] {
  if (prev.length === 0) return snapshotItems.slice(0, MAX_ITEMS)
  const snapshotIds = new Set(snapshotItems.map((item) => item.id))
  const merged = [
    ...snapshotItems,
    ...prev.filter((item) => !snapshotIds.has(item.id)),
  ]
  return merged.slice(0, MAX_ITEMS)
}

function appendPageItems(prev: NotificationItem[], pageItems: NotificationItem[]): NotificationItem[] {
  const ids = new Set(prev.map((item) => item.id))
  const unique = pageItems.filter((item) => !ids.has(item.id))
  return [...prev, ...unique].slice(0, MAX_ITEMS)
}

export interface UseNotificationsResult {
  items: NotificationItem[]
  unreadItems: NotificationItem[]
  unread: number
  unseen: number
  connected: boolean
  loading: boolean
  loadingMore: boolean
  loadingMoreUnread: boolean
  hasMore: boolean
  hasMoreUnread: boolean
  inboxOpen: boolean
  setInboxOpen: React.Dispatch<React.SetStateAction<boolean>>
  preferences: NotificationPreferences
  refresh: () => Promise<void>
  fetchUnreadInitial: () => Promise<void>
  loadMore: () => Promise<void>
  loadMoreUnread: () => Promise<void>
  refreshPreferences: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  markSeen: () => Promise<void>
  dismiss: (id: string) => Promise<void>
}

const NotificationsContext = createContext<UseNotificationsResult | null>(null)

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { getTenantId } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useRef(location)
  const activeTenantId = getTenantId()
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadItems, setUnreadItems] = useState<NotificationItem[]>([])
  const [unread, setUnread] = useState(0)
  const [unseen, setUnseen] = useState(0)
  const [connected, setConnected] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingMoreUnread, setLoadingMoreUnread] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [nextUnreadCursor, setNextUnreadCursor] = useState<string | null>(null)
  const [inboxOpen, setInboxOpen] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES)
  const [isStreamOwner, setIsStreamOwner] = useState(false)
  const connRef = useRef<NotificationStreamConnection | null>(null)
  const preferencesRef = useRef(preferences)
  const isStreamOwnerRef = useRef(isStreamOwner)

  locationRef.current = location
  isStreamOwnerRef.current = isStreamOwner

  useEffect(() => {
    preferencesRef.current = preferences
  }, [preferences])

  useEffect(() => {
    registerNotificationSoundUnlock()
  }, [])

  useEffect(() => initNotificationToastTabGate(), [])

  useEffect(() => subscribeNotificationStreamOwnership(setIsStreamOwner), [])

  useEffect(() => {
    notificationAlertCoordinator.setHandlers({
      openInbox: () => setInboxOpen(true),
      navigate: (path) => navigate(path),
    })
  }, [navigate])

  useEffect(() => {
    if (isAuthenticated) {
      notificationAlertCoordinator.beginCatchUpQuietPeriod()
    } else {
      notificationAlertCoordinator.reset()
    }
  }, [isAuthenticated, activeTenantId])

  const refreshPreferences = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const prefs = await notificationsApi.getPreferences()
      setPreferences(prefs)
    } catch {
      /* keep prior preferences */
    }
  }, [isAuthenticated])

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const [list, counts] = await Promise.all([
        notificationsApi.list({ limit: PAGE_SIZE }),
        notificationsApi.counts(),
      ])
      setItems(list.items)
      setNextCursor(list.next_cursor ?? null)
      setUnread(counts.unread)
      setUnseen(counts.unseen)
      if (isStreamOwnerRef.current) {
        publishNotificationTabSync({
          type: 'snapshot',
          snapshot: {
            items: list.items,
            unread: counts.unread,
            unseen: counts.unseen,
          },
        })
      }
    } catch {
      /* offline / service down — keep prior state */
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchUnreadInitial = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const list = await notificationsApi.list({ limit: PAGE_SIZE, unread: true })
      setUnreadItems(list.items)
      setNextUnreadCursor(list.next_cursor ?? null)
    } catch {
      /* keep current unread state */
    }
  }, [isAuthenticated])

  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const list = await notificationsApi.list({ limit: PAGE_SIZE, before: nextCursor })
      setItems((prev) => appendPageItems(prev, list.items))
      setNextCursor(list.next_cursor ?? null)
    } catch {
      /* keep current page state */
    } finally {
      setLoadingMore(false)
    }
  }, [isAuthenticated, nextCursor, loadingMore])

  const loadMoreUnread = useCallback(async () => {
    if (!isAuthenticated || !nextUnreadCursor || loadingMoreUnread) return
    setLoadingMoreUnread(true)
    try {
      const list = await notificationsApi.list({ limit: PAGE_SIZE, unread: true, before: nextUnreadCursor })
      setUnreadItems((prev) => appendPageItems(prev, list.items))
      setNextUnreadCursor(list.next_cursor ?? null)
    } catch {
      /* keep current page state */
    } finally {
      setLoadingMoreUnread(false)
    }
  }, [isAuthenticated, nextUnreadCursor, loadingMoreUnread])

  const applyIncomingNotification = useCallback((item: NotificationItem, { alert }: { alert: boolean }) => {
    if (activeTenantId && item.tenant_id && item.tenant_id !== activeTenantId) {
      return
    }
    setItems((prev) => mergeItem(prev, item))
    if (!item.read) setUnreadItems((prev) => mergeItem(prev, item))
    setUnread((prev) => prev + (item.read ? 0 : 1))
    setUnseen((prev) => prev + (item.seen ? 0 : 1))
    if (!alert) return
    const prefs = preferencesRef.current
    if (!prefs.in_app_enabled) return
    const { pathname, search } = locationRef.current
    const suppressForActiveChat = shouldSuppressAgentResponseToast(pathname, search, item)
    const showInThisTab = shouldShowNotificationToastInThisTab(getNotificationToastKey(item))
    const showAlert =
      showInThisTab &&
      item.category !== 'access_decision' &&
      !suppressForActiveChat
    notificationAlertCoordinator.notify(item, {
      soundEnabled: prefs.sound_enabled && showAlert,
      showToast: showAlert && Boolean(item.title),
    })
  }, [activeTenantId])

  const applyTabSyncMessage = useCallback((message: NotificationTabSyncMessage) => {
    // Ignore connection fan-out on the stream owner so a prior owner's
    // disconnect during leadership transfer cannot clear a healthy local SSE.
    if (message.type === 'connected' && isStreamOwnerRef.current) return

    switch (message.type) {
      case 'snapshot':
        setUnread(message.snapshot.unread)
        setUnseen(message.snapshot.unseen)
        setLoading(false)
        setItems((prev) => mergeSnapshotItems(prev, message.snapshot.items))
        setUnreadItems((prev) => mergeSnapshotItems(prev, message.snapshot.items.filter((n) => !n.read)))
        break
      case 'notification':
        applyIncomingNotification(message.item, { alert: false })
        break
      case 'counts':
        setUnread(message.counts.unread)
        setUnseen(message.counts.unseen)
        break
      case 'connected':
        setConnected(message.connected)
        break
      case 'mark_read':
        setItems((prev) =>
          prev.map((n) => (n.id === message.id ? { ...n, read: true, seen: true } : n)),
        )
        setUnreadItems((prev) => prev.filter((n) => n.id !== message.id))
        setUnread(message.counts.unread)
        setUnseen(message.counts.unseen)
        break
      case 'mark_all_read':
        setItems((prev) => prev.map((n) => ({ ...n, read: true, seen: true })))
        setUnreadItems([])
        setUnread(0)
        setUnseen(0)
        break
      case 'mark_seen':
        setUnseen(0)
        setItems((prev) => prev.map((n) => ({ ...n, seen: true })))
        break
      case 'dismiss':
        setItems((prev) => prev.filter((n) => n.id !== message.id))
        setUnreadItems((prev) => prev.filter((n) => n.id !== message.id))
        break
      default:
        break
    }
  }, [applyIncomingNotification])

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      void refresh()
      void refreshPreferences()
    } else {
      setItems([])
      setUnreadItems([])
      setNextCursor(null)
      setNextUnreadCursor(null)
      setUnread(0)
      setUnseen(0)
      setConnected(false)
      setLoading(false)
    }
  }, [isAuthenticated, refresh, refreshPreferences, activeTenantId])

  // SSE stream — only the primary visible tab holds the connection.
  // Location is read from a ref so SPA navigations do not reconnect.
  useEffect(() => {
    if (!isAuthenticated || !isStreamOwner) return

    const conn = subscribeToNotifications({
      onOpen: () => {
        setConnected(true)
        publishNotificationTabSync({ type: 'connected', connected: true })
      },
      onError: () => {
        setConnected(false)
        publishNotificationTabSync({ type: 'connected', connected: false })
      },
      onSnapshot: (snapshot) => {
        setUnread(snapshot.unread)
        setUnseen(snapshot.unseen)
        setLoading(false)
        setItems((prev) => mergeSnapshotItems(prev, snapshot.items))
        setUnreadItems((prev) => mergeSnapshotItems(prev, snapshot.items.filter((n) => !n.read)))
        publishNotificationTabSync({ type: 'snapshot', snapshot })
      },
      onNotification: (item) => {
        applyIncomingNotification(item, { alert: true })
        publishNotificationTabSync({ type: 'notification', item })
      },
    })
    connRef.current = conn
    return () => {
      conn.close()
      connRef.current = null
      setConnected(false)
      publishNotificationTabSync({ type: 'connected', connected: false })
    }
  }, [isAuthenticated, activeTenantId, isStreamOwner, applyIncomingNotification])

  // Cross-tab sync for followers (live SSE fan-out) and all tabs (mutations).
  useEffect(() => {
    if (!isAuthenticated) return
    return subscribeNotificationTabSync(applyTabSyncMessage)
  }, [isAuthenticated, applyTabSyncMessage])

  // Polling fallback — stream owner only, and only while SSE is down.
  useEffect(() => {
    if (!isAuthenticated || !isStreamOwner || connected) return

    const interval = setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isAuthenticated, isStreamOwner, connected, refresh])

  const markRead = useCallback(async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true, seen: true } : n)))
    setUnreadItems((prev) => prev.filter((n) => n.id !== id))
    try {
      const counts = await notificationsApi.markRead(id)
      setUnread(counts.unread)
      setUnseen(counts.unseen)
      publishNotificationTabSync({ type: 'mark_read', id, counts })
    } catch {
      void refresh()
    }
  }, [refresh])

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true, seen: true })))
    setUnreadItems([])
    try {
      await notificationsApi.markAllRead()
      setUnread(0)
      setUnseen(0)
      publishNotificationTabSync({ type: 'mark_all_read' })
    } catch {
      void refresh()
    }
  }, [refresh])

  const markSeen = useCallback(async () => {
    setUnseen(0)
    setItems((prev) => prev.map((n) => ({ ...n, seen: true })))
    try {
      await notificationsApi.markSeen()
      publishNotificationTabSync({ type: 'mark_seen' })
    } catch {
      /* best-effort */
    }
  }, [])

  const dismiss = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((n) => n.id !== id))
    setUnreadItems((prev) => prev.filter((n) => n.id !== id))
    try {
      await notificationsApi.dismiss(id)
      publishNotificationTabSync({ type: 'dismiss', id })
    } catch {
      void refresh()
    }
  }, [refresh])

  return React.createElement(
    NotificationsContext.Provider,
    {
      value: {
        items,
        unreadItems,
        unread,
        unseen,
        connected,
        loading,
        loadingMore,
        loadingMoreUnread,
        hasMore: nextCursor !== null,
        hasMoreUnread: nextUnreadCursor !== null,
        inboxOpen,
        setInboxOpen,
        preferences,
        refresh,
        fetchUnreadInitial,
        loadMore,
        loadMoreUnread,
        refreshPreferences,
        markRead,
        markAllRead,
        markSeen,
        dismiss,
      },
    },
    children
  )
}

export function useNotifications(): UseNotificationsResult {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}
